import type { MouseEventHandler, ReactNode } from "react"
import {
  Archive,
  ArchiveRestore,
  Bell,
  BellOff,
  Car,
  LogOut,
  MessageCircle,
  MoreVertical,
  TriangleAlert,
  UserRoundPen,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarTrigger,
} from "@/components/ui/menubar"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import WidgetLayoutControls from "@/components/chat/WidgetLayoutControls"
import { useTranslation } from "react-i18next"

type RoomActions = {
  edit?: MouseEventHandler<HTMLElement>
  info?: { kind: "car" | "chat"; label: string; onOpen: () => void }
  issueLines: string[]
  muted: boolean
  onToggleMuted: () => void
  archived: boolean
  onToggleArchived: () => void
  leave?: MouseEventHandler<HTMLElement>
}

type Props = {
  variant: "agent" | "classic"
  hidden: boolean
  actions: RoomActions
}

const IssueDetails = ({
  lines,
  side,
}: {
  lines: string[]
  side: "left" | "bottom"
}) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex items-center text-amber-500">
          <TriangleAlert className="h-3.5 w-3.5" aria-hidden="true" />
        </span>
      </TooltipTrigger>
      <TooltipContent side={side}>
        <ul className="space-y-1 text-xs">
          {lines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
)

const AgentActionButton = ({
  label,
  onClick,
  children,
}: {
  label: string
  onClick: MouseEventHandler<HTMLElement>
  children: ReactNode
}) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={label}
          title={label}
          onClick={onClick}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
)

const ChatHeaderRoomActions = ({ variant, hidden, actions }: Props) => {
  const { t } = useTranslation()
  const agent = variant === "agent"
  const {
    edit,
    info,
    issueLines,
    muted,
    onToggleMuted,
    archived,
    onToggleArchived,
    leave,
  } = actions
  const hasIssues = issueLines.length > 0

  return (
    <>
      {!hidden && agent && edit && (
        <AgentActionButton label={t("roomActions.editDriver")} onClick={edit}>
          <UserRoundPen className="h-4 w-4" />
        </AgentActionButton>
      )}
      {!hidden && agent && info && (
        <AgentActionButton label={info.label} onClick={info.onOpen}>
          {info.kind === "car" ? (
            <Car className="h-4 w-4" />
          ) : (
            <MessageCircle className="h-4 w-4" />
          )}
        </AgentActionButton>
      )}
      {!hidden && agent && hasIssues && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t("driverIssues.warning")}
                title={issueLines.join("\n")}
                onClick={info?.onOpen}
              >
                <TriangleAlert className="h-4 w-4 text-amber-500" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <ul className="space-y-1 text-xs">
                {issueLines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {!hidden && (
        <Menubar>
          <MenubarMenu>
            <MenubarTrigger asChild>
              <Button
                data-testid="action-menu"
                variant="ghost"
                size={!agent && hasIssues ? undefined : "icon"}
                className={
                  !agent && hasIssues
                    ? "flex h-9 w-auto items-center gap-1 px-2"
                    : undefined
                }
                aria-label={
                  !agent && hasIssues
                    ? t("roomActions.openMenuWarning")
                    : t("roomActions.openMenu")
                }
                title={agent ? t("roomActions.openMenu") : undefined}
              >
                {!agent && hasIssues && (
                  <IssueDetails lines={issueLines} side="bottom" />
                )}
                <MoreVertical className="h-4 w-4" />
              </Button>
            </MenubarTrigger>
            <MenubarContent className="w-56" align="end">
              {!agent && edit && (
                <MenubarItem onClick={edit}>
                  <UserRoundPen className="mr-2 h-4 w-4" />
                  <span>{t("roomActions.edit")}</span>
                </MenubarItem>
              )}
              {!agent && info && (
                <MenubarItem onClick={info.onOpen}>
                  {info.kind === "car" ? (
                    <Car className="mr-2 h-4 w-4" />
                  ) : (
                    <MessageCircle className="mr-2 h-4 w-4" />
                  )}
                  <span>{info.label}</span>
                  {hasIssues && (
                    <span className="ml-auto">
                      <IssueDetails lines={issueLines} side="left" />
                    </span>
                  )}
                </MenubarItem>
              )}
              <MenubarItem onClick={onToggleMuted}>
                {muted ? (
                  <Bell className="mr-2 h-4 w-4" />
                ) : (
                  <BellOff className="mr-2 h-4 w-4" />
                )}
                <span>
                  {muted ? t("roomActions.unmute") : t("roomActions.mute")}
                </span>
              </MenubarItem>
              <MenubarItem onClick={onToggleArchived}>
                {archived ? (
                  <ArchiveRestore className="mr-2 h-4 w-4" />
                ) : (
                  <Archive className="mr-2 h-4 w-4" />
                )}
                <span>
                  {archived
                    ? t("roomActions.unarchive")
                    : t("roomActions.archive")}
                </span>
              </MenubarItem>
              {leave && !agent && (info || edit) && <MenubarSeparator />}
              {leave && (
                <MenubarItem onClick={leave}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>{t("roomActions.leave")}</span>
                </MenubarItem>
              )}
            </MenubarContent>
          </MenubarMenu>
        </Menubar>
      )}
      {agent && (
        <>
          <div className="mx-0.5 h-5 w-px shrink-0 bg-border" />
          <WidgetLayoutControls />
        </>
      )}
    </>
  )
}

export default ChatHeaderRoomActions
